export type StyleColorProperty = 'color' | 'background-color';

interface StyleDeclaration {
    property: string;
    value: string;
}

/**
 * Accepts #rgb, #rrggbb, and rgb()/rgba() so pasted table colors can be shown
 * in a color input and written back as a stable hex value.
 */
export function normalizeHexColor(value: string | null | undefined): string | null {
    if (!value) return null;
    const trimmed = value.trim().toLowerCase();
    const short = /^#([0-9a-f]{3})$/.exec(trimmed);
    if (short) {
        const [red, green, blue] = short[1].split('');
        return `#${red}${red}${green}${green}${blue}${blue}`;
    }
    if (/^#[0-9a-f]{6}$/.test(trimmed)) return trimmed;

    const rgb =
        /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*(\d*\.?\d+))?\s*\)$/.exec(
            trimmed
        );
    if (!rgb) return null;
    const channels = [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
    if (channels.some((channel) => !Number.isInteger(channel) || channel > 255)) return null;
    if (rgb[4] !== undefined && Number(rgb[4]) === 0) return null;
    return `#${channels.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

export function parseStyleDeclarations(style: string | null | undefined): StyleDeclaration[] {
    if (!style) return [];
    const parts: string[] = [];
    let current = '';
    let quote: '"' | "'" | null = null;
    for (const char of style) {
        if (quote) {
            current += char;
            if (char === quote) quote = null;
            continue;
        }
        if (char === '"' || char === "'") {
            quote = char;
            current += char;
            continue;
        }
        if (char === ';') {
            parts.push(current);
            current = '';
            continue;
        }
        current += char;
    }
    if (current.trim()) parts.push(current);

    const declarations: StyleDeclaration[] = [];
    parts.forEach((part) => {
        const index = part.indexOf(':');
        if (index === -1) return;
        const property = part.slice(0, index).trim().toLowerCase();
        const value = part.slice(index + 1).trim();
        if (!property || !value) return;
        declarations.push({ property, value });
    });
    return declarations;
}

function joinDeclarations(declarations: StyleDeclaration[]): string | null {
    if (declarations.length === 0) return null;
    return declarations.map((entry) => `${entry.property}: ${entry.value}`).join('; ');
}

export function readStyleColor(
    style: string | null | undefined,
    property: StyleColorProperty
): string | null {
    const found = parseStyleDeclarations(style).find((entry) => entry.property === property);
    return found?.value ?? null;
}

export function setStyleColor(
    style: string | null | undefined,
    property: StyleColorProperty,
    value: string | null
): string | null {
    const hex = value == null ? null : normalizeHexColor(value);
    if (value != null && !hex) {
        const trimmed = style?.trim();
        return trimmed ? trimmed : null;
    }

    const declarations = parseStyleDeclarations(style);
    let seen = false;
    const next: StyleDeclaration[] = [];
    declarations.forEach((entry) => {
        if (entry.property !== property) {
            next.push(entry);
            return;
        }
        if (seen || !hex) return;
        seen = true;
        next.push({ property, value: hex });
    });
    if (!seen && hex) next.push({ property, value: hex });
    return joinDeclarations(next);
}

export function uniformStyleColor(
    styles: Array<string | null | undefined>,
    property: StyleColorProperty
): { uniform: boolean; color: string | null } {
    if (styles.length === 0) return { uniform: true, color: null };
    const values = styles.map((style) => normalizeHexColor(readStyleColor(style, property)));
    const first = values[0] ?? null;
    const uniform = values.every((entry) => entry === first);
    return { uniform, color: uniform ? first : null };
}

/** `bgcolor` is what the retainer PDF renderer uses for cell fills. */
export function cellStyleHtmlAttributes(style: unknown): Record<string, string> {
    if (typeof style !== 'string' || !style.trim()) return {};
    const background = normalizeHexColor(readStyleColor(style, 'background-color'));
    if (!background) return { style };
    return { style, bgcolor: background };
}
